import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useApiStore } from "@/stores/api";

/**
 * Centraliza o avanço entre telas da jornada.
 *
 * Mantém `isAdvancing` ativo até a tela desmontar: o flag só é resetado em erro
 * ou quando a navegação não ocorre. Assim o botão de avançar permanece em
 * loading até a próxima tela aparecer.
 *
 * `showComplete` reflete o estado de conclusão da etapa (`phase-completed`),
 * mas congelado enquanto avança: durante a navegação o guard atualiza o
 * checklist, e sem o congelamento a tela piscaria trocando entre a visão de
 * input e a de "etapa concluída". Passe `isComplete` (um getter) para as telas
 * que renderizam `phase-completed`.
 */
export function useStepFlow(pathPage: string, isComplete?: () => boolean) {
  const router = useRouter();
  const api = useApiStore();

  const isAdvancing = ref(false);
  const completeAtAdvanceStart = ref(false);

  const showComplete = computed(() => {
    if (isComplete == null) {
      return false;
    }
    return isAdvancing.value ? completeAtAdvanceStart.value : isComplete();
  });

  async function goNext(): Promise<boolean> {
    const nextRoute = await api.sequenceScreenFlow(pathPage, true);
    if (nextRoute) {
      await router.push(nextRoute);
      return true;
    }
    return false;
  }

  async function advance(action?: () => Promise<unknown>): Promise<void> {
    if (isAdvancing.value) {
      return;
    }
    completeAtAdvanceStart.value = isComplete != null ? isComplete() : false;
    isAdvancing.value = true;
    try {
      if (action != null) {
        await action();
      }
      const navigated = await goNext();
      if (!navigated) {
        isAdvancing.value = false;
      }
    } catch (error) {
      isAdvancing.value = false;
      throw error;
    }
  }

  return { isAdvancing, showComplete, advance, goNext };
}
